import { apiGet, APIURL, cancelSelectEvent, getCSSStyles, getHeaderPinButtonHTML, getHeaderPinStateClass, getNoDataHTML, getWidgetDataURI, isHeaderPinned, normalizeWidgetTheme, setupHeaderPin, toggleWidgetTheme } from './../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from './../../types';

export class TraderverseCryptoMarketCap {
  private container: HTMLElement;
  private config: WidgetConfig;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private updateInterval: number | null = null;
  private shadowRoot: ShadowRoot;
  private currentData: any = null;
  private widgetResizeObserver: ResizeObserver | null = null;
  private lastFetchedAt: Date | null = null;

  constructor(config: WidgetConfig) {
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.container = this.resolveContainer(config.container);

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    }

    this.init();

    this.widgetResizeObserver = new ResizeObserver(() => this.updateDisplay());
    setTimeout(() => {
      const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      if (widget) this.widgetResizeObserver?.observe(widget);
    }, 0);
  }

  private fetchSymbols(): Array<{ value: string; label: string }> {
    return [
      { label: 'AAPL', value: 'AAPL' },
      { label: 'MSFT', value: 'MSFT' },
      { label: 'GOOGL', value: 'GOOGL' },
      { label: 'AMZN', value: 'AMZN' },
      { label: 'NVDA', value: 'NVDA' },
      { label: 'META', value: 'META' },
      { label: 'TSLA', value: 'TSLA' },
      { label: 'AVGO', value: 'AVGO' },
      { label: 'PEP', value: 'PEP' },
      { label: 'COST', value: 'COST' }
    ];
  }

  private getDefaultConfig(): Partial<WidgetConfig> {
    const symbols = this.fetchSymbols();
    return {
      headerPinned: true,
      symbol: symbols[0].value,
      theme: 'default-dark',
      width: '100%',
      height: '400px',
      autosize: true
    };
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const el = document.getElementById(container) || document.querySelector(container);
      if (!el) throw new Error(`Container not found: ${container}`);
      return el as HTMLElement;
    }
    return container;
  }

  private async init(): Promise<void> {
    if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;

    this.createWidget();
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) widget.classList.add('loading');
    await this.injectChartLibrary();
    this.attachStyles();
    await this.setupChart();
    if (widget) widget.classList.remove('loading');
    this.emit('ready', {});
    this.config.onReady?.();
    cancelSelectEvent(this.shadowRoot);
  }

  private async injectChartLibrary(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if ((window as any).echarts) return resolve(true);
      if (this.shadowRoot.querySelector('script[data-chart-lib]')) {
        const script = this.shadowRoot.querySelector('script[data-chart-lib]') as HTMLScriptElement;
        script.onload = () => resolve(true);
      } else {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
        script.async = true;
        script.setAttribute('data-chart-lib', 'true');
        this.shadowRoot.appendChild(script);
        script.onload = () => resolve(true);
      }
    });
  }

  private createWidget(): void {
    let themeClass = 'defaultDarkMode';
  let dataTheme = 'default-dark';
  
  if (this.config.theme === 'white') {
    themeClass = 'whiteMode';
    dataTheme = 'white';
  } else if (this.config.theme === 'black') {
    themeClass = 'blackMode';
    dataTheme = 'black';
  } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}
    const widgetHtml = `
      <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
        <div class="headTop">
          <h4 class="title crypto">${this.getTitleIconSvg()} Market Cap</h4>
          <div class="actionCol">${getHeaderPinButtonHTML(this.config)}<tooltip-wrapper id="lastUpdatedTooltip"><svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M0.625 6.625C0.625 7.41293 0.780195 8.19315 1.08172 8.9211C1.38325 9.64905 1.82521 10.3105 2.38236 10.8676C2.93951 11.4248 3.60095 11.8667 4.3289 12.1683C5.05685 12.4698 5.83707 12.625 6.625 12.625C7.41293 12.625 8.19315 12.4698 8.9211 12.1683C9.64905 11.8667 10.3105 11.4248 10.8676 10.8676C11.4248 10.3105 11.8667 9.64905 12.1683 8.9211C12.4698 8.19315 12.625 7.41293 12.625 6.625C12.625 5.0337 11.9929 3.50758 10.8676 2.38236C9.74242 1.25714 8.2163 0.625 6.625 0.625C5.0337 0.625 3.50758 1.25714 2.38236 2.38236C1.25714 3.50758 0.625 5.0337 0.625 6.625Z" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/> <path d="M6.625 3.2915V6.62484L8.625 8.62484" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/> </svg><tooltip><span>Last Updated</span><strong>N/A</strong></tooltip></tooltip-wrapper><i class="pi pi-ellipsis-v"></i></div>
        </div>

        <div class="inBody">
          <div id="marketCapSummary" data-testing-id="market-cap-summary"></div>
          <div id="cryptoContainer" data-testing-id="crypto-container"></div>
        </div>
      </div>
    `;
    this.shadowRoot.innerHTML = widgetHtml;
    setupHeaderPin(this.shadowRoot, this.config);
  }

  private async attachStyles(): Promise<void> {
    try {
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error('Failed to load CSS file:', err);
    }
  }

  private async setupChart(): Promise<void> {
    try {
      const [topIndexRes, marketCapRes] = await Promise.all([
        apiGet({
          method: 'GET',
          url: `${APIURL}/v1/crypto/top-index?topCoin=top_100`,
          body: { topCoin: 'top_100' },
          headers: { 'Content-Type': 'application/json' }
        }),
        apiGet({
          method: 'GET',
          url: `${APIURL}/v1/crypto/crypto_market_cap_chart?interval=30d`,
          body: { interval: '30d' },
          headers: { 'Content-Type': 'application/json' }
        })
      ]);

      if (!topIndexRes.ok || !marketCapRes.ok)
        throw new Error("Failed to fetch one or both API responses");

      const [topIndexData, marketCapData] = await Promise.all([
        topIndexRes.json(),
        marketCapRes.json()
      ]);

      this.currentData = { topIndex: topIndexData, marketCap: marketCapData };
      const fromApi = (topIndexData as any)?.last_updated_at ?? (marketCapData as any)?.last_updated_at;
      this.lastFetchedAt = fromApi ? new Date(fromApi) : new Date();
      this.updateDisplay();
      this.emit('dataUpdate', this.currentData);
    } catch (err) {
      console.error("Error fetching chart data:", err);
    }
  }

  private trendingCoins(): void {
    if (!this.currentData?.topIndex?.data) return;
    this.renderTrendingCoins(this.currentData.topIndex.data);
  }

  private renderTrendingCoins(cryptoData: any): void {
    const container = this.shadowRoot?.querySelector('#cryptoContainer');
    if (!container) return;

    if (!cryptoData) {
      container.innerHTML = getNoDataHTML();
      return;
    }

    const currentIndexValue = cryptoData.currentIndexValue ?? 0;
    const percentChange = cryptoData.percentChange ?? 0;
    const changeClass = percentChange >= 0 ? 'positive' : 'negative';
    const changeSymbol = percentChange >= 0 ? '+' : '';

    container.innerHTML = `
      <div class="cryptoCard" data-testing-id="top-100-index-card">
        <h2 data-testing-id="top-100-index-title">Top 100 Coin Index</h2>
        <div class="chartFig">
          <div class="valueWrapper" data-testing-id="index-value-wrapper">
            <h3 data-testing-id="index-value">$${currentIndexValue.toFixed(2)}T</h3>
            <span class="${changeClass}" data-testing-id="index-change" data-change="${percentChange}">${changeSymbol}${percentChange.toFixed(2)}% (24h)</span>
          </div>
          <div class="inChart"><div id="cryptoMarketChart" data-testing-id="crypto-market-chart"></div></div>
        </div>
      </div>
    `;

    this.drawChart();
  }

  private drawChart(): void {
    const echarts = (window as any).echarts;
    const container = this.shadowRoot.querySelector('#cryptoMarketChart') as HTMLElement;
    if (!container || !this.currentData?.topIndex?.data?.chartData) return;

    const chartData = this.currentData.topIndex?.data.chartData;
    const seriesData = chartData.map((item: any) => [item.timestamp, item.value]);

    const instance = echarts.getInstanceByDom(container);
    if (instance) echarts.dispose(instance);

    const chart = echarts.init(container);
    const option = {
      backgroundColor: 'transparent',
      grid: {
        left: 0,
        right: 0,
        top: 0,
        bottom: 0
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        show: false
      },
      yAxis: {
        type: 'value',
        show: false
      },
      series: [{
        data: seriesData.map((item: any) => item[1]),
        type: 'line',
        smooth: true,
        symbol: 'none',
        lineStyle: {
          width: 1.5,
          color: '#00E396'
        },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: 'rgba(0, 227, 150, 0.25)' },
            { offset: 1, color: 'rgba(0, 227, 150, 0)' }
          ])
        }
      }]
    };

    chart.setOption(option);
    
    // Add data-testing-id to chart after render
    setTimeout(() => {
      const chartSvg = this.shadowRoot.querySelector('#cryptoMarketChart svg');
      if (chartSvg) {
        chartSvg.setAttribute('data-testing-id', 'crypto-market-chart-svg');
      }
    }, 500);
  }

  private formatCurrency(value: number | null): string {
    if (value == null) return "-";
    return "$" + (value >= 1e9
      ? (value / 1e9).toFixed(2) + "B"
      : value >= 1e6
        ? (value / 1e6).toFixed(2) + "M"
        : value.toLocaleString());
  }

  private formatDate(dateStr?: string | null): string {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }

  private renderMarketCapSummary(): void {
    const summaryContainer = this.shadowRoot.querySelector('#marketCapSummary');
    if (!summaryContainer || !this.currentData?.marketCap?.data?.length) return;
    const hasData = !!this.currentData?.marketCap?.data?.length;

if (hasData) {
  summaryContainer.setAttribute('data-testing-id', 'market-cap-summary');
} else {
  summaryContainer.removeAttribute('data-testing-id');
}

    const block = this.currentData.marketCap.data[0];
    const currentBlock = block.current?.[0];
    const currentRec = currentBlock?.records?.[0];
    const yesterdayRec = currentBlock?.records?.[1];
    const lastWeekRec = block.lastWeek?.[0];
    const lastMonthRec = block.lastMonth?.[0];
    const yearlyHigh = block.yearlyHighest?.[0];
    const yearlyLow = block.yearlyLowest?.[0];

    const currentCap = currentRec?.quote?.USD?.total_market_cap ?? null;
    const yesterdayCap = yesterdayRec?.quote?.USD?.total_market_cap ?? null;
    const lastWeekCap = lastWeekRec?.quote?.USD?.total_market_cap ?? null;
    const lastMonthCap = lastMonthRec?.quote?.USD?.total_market_cap ?? null;
    const yearlyHighCap = yearlyHigh?.quote?.USD?.total_market_cap ?? null;
    const yearlyLowCap = yearlyLow?.quote?.USD?.total_market_cap ?? null;

    const dailyChange = currentBlock?.todayChangePercent ?? 0;
    const yesterdayChange = yesterdayRec?.yesterdayChangePercent ?? 0;
    const lastWeekChange = lastWeekRec?.lastWeekChangePercent ?? 0;
    const lastMonthChange = lastMonthRec?.lastMonthChangePercent ?? 0;

    const yearlyHighPercent =
      currentCap && yearlyHighCap ? ((currentCap / yearlyHighCap) * 100).toFixed(2) : "-";
    const yearlyLowPercent =
      currentCap && yearlyLowCap ? ((currentCap / yearlyLowCap) * 100).toFixed(2) : "-";

    const changeClass = (val: number) => val >= 0 ? "positive" : "negative";
    const sign = (val: number) => (val >= 0 ? "+" : "");

    summaryContainer.innerHTML = `
    <div class="cryptoCard" style="margin-bottom: 20px" data-testing-id="market-cap-card">
      <h2 data-testing-id="market-cap-title">Crypto Market Cap</h2>
      <div class="valueWrapper" data-testing-id="market-cap-value-wrapper">
        <h3 data-testing-id="market-cap-value">${this.formatCurrency(currentCap)}</h3>
        <span class="${changeClass(dailyChange)}" data-testing-id="market-cap-change" data-change="${dailyChange}"> ${sign(dailyChange)}${dailyChange.toFixed(2)}% (24h) </span>
      </div>
    </div>

    <div class="cryptoCard" style="margin-bottom: 20px" data-testing-id="historical-values-card">
      <h2 data-testing-id="historical-title">Market Cap Historical Values</h2>
      <div class="valueWrapper" data-testing-id="historical-yesterday" data-period="yesterday">
        <p data-testing-id="period-label">Yesterday</p>
        <span class="expand"></span>
        <span data-testing-id="period-value">${this.formatCurrency(yesterdayCap)}</span>
        <span class="${changeClass(yesterdayChange)}" data-testing-id="period-change" data-change="${yesterdayChange}"> ${sign(yesterdayChange)}${yesterdayChange.toFixed(2)}% </span>
      </div>
      <div class="valueWrapper" data-testing-id="historical-last-week" data-period="lastWeek">
        <p data-testing-id="period-label">Last Week</p>
        <span class="expand"></span>
        <span data-testing-id="period-value">${this.formatCurrency(lastWeekCap)}</span>
        <span class="${changeClass(lastWeekChange)}" data-testing-id="period-change" data-change="${lastWeekChange}"> ${sign(lastWeekChange)}${lastWeekChange.toFixed(2)}% </span>
      </div>
      <div class="valueWrapper" data-testing-id="historical-last-month" data-period="lastMonth">
        <p data-testing-id="period-label">Last Month</p>
        <span class="expand"></span>
        <span data-testing-id="period-value">${this.formatCurrency(lastMonthCap)}</span>
        <span class="${changeClass(lastMonthChange)}" data-testing-id="period-change" data-change="${lastMonthChange}"> ${sign(lastMonthChange)}${lastMonthChange.toFixed(2)}% </span>
      </div>
    </div>
    
    <div class="cryptoCard" style="margin-bottom: 20px" data-testing-id="yearly-performance-card">
      <h2 data-testing-id="yearly-title">Market Cap Yearly Performance</h2>
      <div class="valueWrapper" data-testing-id="yearly-high" data-type="high">
        <p data-testing-id="yearly-date">Yearly High (${this.formatDate(yearlyHigh?.quote?.USD?.timestamp)})</p>
        <span class="expand"></span>
        <span data-testing-id="yearly-percent" data-percent="${yearlyHighPercent}">${yearlyHighPercent}%</span>
      </div>
      <div class="valueWrapper" data-testing-id="yearly-low" data-type="low">
        <p data-testing-id="yearly-date">Yearly Low (${this.formatDate(yearlyLow?.quote?.USD?.timestamp)})</p>
        <span class="expand"></span>
        <span data-testing-id="yearly-percent" data-percent="${yearlyLowPercent}">${yearlyLowPercent}%</span>
      </div>
    </div>
  `;
  }

  private formatLastUpdated(date: Date): string {
    const options: Intl.DateTimeFormatOptions = {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/New_York',
    };
    return date.toLocaleString('en-US', options).replace(',', '').replace(/\//g, '-') + ' EST';
  }

  private updateLastUpdatedTooltip(): void {
    const lastUpdated = this.lastFetchedAt ? this.formatLastUpdated(this.lastFetchedAt) : 'N/A';
    const tooltip = this.shadowRoot.querySelector('#lastUpdatedTooltip tooltip strong');
    if (tooltip) tooltip.innerHTML = lastUpdated.replace(' ', '<br>');
  }

  private updateDisplay(): void {
    this.updateLastUpdatedTooltip();
    if (!this.currentData) {
      setTimeout(() => this.updateDisplay(), 100);
      return;
    }
    this.trendingCoins();
    this.renderMarketCapSummary();
  }

  public on(type: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
    this.eventHandlers.get(type)!.push(handler);
  }

  public off(type: WidgetEventType, handler: WidgetEventHandler): void {
    const list = this.eventHandlers.get(type);
    if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
  }

  public setSymbol(symbol: string): void {
    this.config.symbol = symbol;
    this.setupChart();
  }

  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    return await getWidgetDataURI(widget);
  }
  
  public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
    if (!this.shadowRoot) return;
  
    this.config.theme = toggleWidgetTheme(
      this.shadowRoot,
      theme
    ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
  
    this.updateDisplay();
  }

  public destroy(): void {
    if (this.updateInterval) { clearInterval(this.updateInterval); this.updateInterval = null; }
    if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
    this.eventHandlers.clear(); this.shadowRoot.innerHTML = '';
  }

  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
  }
  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M15.15 20.3C12.3125 20.3 10 17.9875 10 15.15C10 12.3125 12.3125 10 15.15 10C17.9875 10 20.3 12.3125 20.3 15.15C20.3 17.9875 18 20.3 15.15 20.3ZM15.15 11.8625C13.3375 11.8625 11.875 13.3375 11.875 15.1375C11.875 16.9375 13.35 18.4125 15.15 18.4125C16.95 18.4125 18.425 16.9375 18.425 15.1375C18.425 13.3375 16.9625 11.8625 15.15 11.8625Z" fill="currentColor"/> <path d="M15 28.4375C7.5875 28.4375 1.5625 22.4125 1.5625 15C1.5625 7.5875 7.5875 1.5625 15 1.5625C22.4125 1.5625 28.4375 7.5875 28.4375 15C28.4375 22.4125 22.4125 28.4375 15 28.4375ZM15 3.4375C8.625 3.4375 3.4375 8.625 3.4375 15C3.4375 21.375 8.625 26.5625 15 26.5625C21.375 26.5625 26.5625 21.375 26.5625 15C26.5625 8.625 21.375 3.4375 15 3.4375Z" fill="currentColor"/> <path d="M15.0625 22C14.5158 22 14.0625 21.5192 14.0625 20.9394V9.06061C14.0625 8.48081 14.5158 8 15.0625 8C15.6092 8 16.0625 8.48081 16.0625 9.06061V20.9394C16.0625 21.5192 15.6092 22 15.0625 22Z" fill="currentColor"/> </svg>`;
  }

}

declare global { interface Window { TraderverseCryptoMarketCap: typeof TraderverseCryptoMarketCap; } }
if (typeof window !== 'undefined') { window.TraderverseCryptoMarketCap = TraderverseCryptoMarketCap; }